/**
 * Allys Area Hero Component
 * 
 * Full-screen hero with area image or video background and scroll indicator.
 * When heroVideo is present, renders a muted autoplay video with the image as poster/fallback.
 * Designed for SEO-focused area pages.
 */

'use client';

import { ArrowDown } from '@phosphor-icons/react';
import Image from 'next/image';
import type { AreaHeroProps } from '@routeroyal/theme-core';

export interface AllysAreaHeroProps extends AreaHeroProps {
  /**
   * Custom overlay opacity (0-1)
   * @default 0.3
   */
  overlayOpacity?: number;
}

export function AllysAreaHero({
  area,
  showScrollIndicator = true,
  overlayOpacity = 0.3,
  className = '',
}: AllysAreaHeroProps) {
  const hasVideo = !!area.heroVideo;

  return (
    <div className={`relative h-[85vh] md:h-screen w-full overflow-hidden ${className}`}>
      {/* Background Media */}
      <div className="absolute inset-0 z-0">
        {/* Gradient Overlay */}
        <div 
          className="absolute inset-0 z-10 bg-gradient-to-b from-black/40 via-transparent to-black/30"
          style={{ opacity: overlayOpacity + 0.2 }}
        />
        
        {hasVideo ? (
          <video
            autoPlay
            muted
            loop
            playsInline
            poster={area.heroImage}
            className="absolute inset-0 h-full w-full object-cover animate-subtle-zoom"
          >
            <source src={area.heroVideo} type="video/mp4" />
          </video>
        ) : (
          <Image
            src={area.heroImage}
            alt={`${area.name}, ${area.region} - Mauritius`}
            fill
            priority
            className="object-cover animate-subtle-zoom"
            sizes="100vw"
          />
        )}
      </div>

      {/* Scroll Indicator */}
      {showScrollIndicator && (
        <div 
          className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20 text-white/80 flex flex-col items-center gap-2 animate-bounce-gentle"
        >
          <span 
            className="text-[10px] tracking-[0.3em] uppercase drop-shadow-md font-inter"
          >
            Scroll
          </span>
          <ArrowDown size={20} className="drop-shadow-md" weight="light" />
        </div>
      )}

      {/* Custom Styles */}
      <style jsx>{`
        @keyframes subtle-zoom {
          from {
            transform: scale(1.05);
          }
          to {
            transform: scale(1);
          }
        }
        
        @keyframes bounce-gentle {
          0%, 100% {
            transform: translateX(-50%) translateY(0);
          }
          50% {
            transform: translateX(-50%) translateY(8px);
          }
        }
        
        .animate-subtle-zoom {
          animation: subtle-zoom 2s ease-out forwards;
        }
        
        .animate-bounce-gentle {
          animation: bounce-gentle 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
